import React, { useState, useEffect, useMemo } from 'react'
import Select from 'react-select'
import { API_ENDPOINTS } from '../../../config/api'
import offlineApi from '../../../utils/offlineApi'
import useCashierProducts from '../../../hooks/useCashierProducts'
import apiClient from '../../../config/api'
import Swal from 'sweetalert2'

const brandColors = {
  primary: '#1338b5',
  mid: '#1f4dc7',
  bright: '#5c8ef0',
}

const CreateLayby = ({ userInfo, branchId, onSuccess }) => {
  const { products, loading: productsLoading } = useCashierProducts()
  const [normalizedProducts, setNormalizedProducts] = useState([])
  const [selectedItems, setSelectedItems] = useState([]) // Array of { product, quantity }
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [deposit, setDeposit] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // Payment methods (same as Checkout.js)
  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'usd_cash', label: 'USD Cash' },
    { value: 'zig_cash', label: 'ZIG Cash' },
    { value: 'ecocash_usd', label: 'Ecocash USD' },
    { value: 'ecocash_zig', label: 'Ecocash ZIG' },
    { value: 'bank_transfer_zig', label: 'Bank Transfer ZIG' },
    { value: 'bank_transfer_usd', label: 'Bank Transfer USD' },
    { value: 'card', label: 'Card' },
  ]

  // Normalize products similar to NewSale.js
  useEffect(() => {
    if (!products?.length) return

    const normalized = products.map((item) => {
      const stock = Number(item.quantity)
      const price = Number(item.selling_price)
      
      let productId = null
      if (item.product !== undefined && item.product !== null) {
        productId = typeof item.product === 'object' ? item.product.id : item.product
      } else if (item.product_id !== undefined) {
        productId = item.product_id
      }

      return {
        id: item.id ?? item.product_id ?? item.product_sku,
        product_id: productId,
        name: item.product_name || 'Unnamed product',
        sku: item.product_sku || 'N/A',
        price: Number.isFinite(price) ? price : 0,
        stock: Number.isFinite(stock) ? stock : 0,
        category:
          item.category_name ||
          item.product_category ||
          item.category ||
          'General',
      }
    })

    setNormalizedProducts(normalized)
  }, [products])

  // Prepare options for react-select
  const productOptions = useMemo(() => {
    return normalizedProducts
      .filter((p) => p.stock > 0) // Only show in-stock items
      .map((product) => ({
        value: product.product_id,
        label: `${product.name} (SKU: ${product.sku}) - ${product.price.toFixed(2)} - Stock: ${product.stock}`,
        product: product,
      }))
  }, [normalizedProducts])

  const handleProductSelect = (selectedOption) => {
    if (!selectedOption) return

    const product = selectedOption.product
    const existingItem = selectedItems.find((item) => item.product.product_id === product.product_id)

    if (existingItem) {
      // If already selected, increase quantity (up to stock limit)
      setSelectedItems((prev) =>
        prev.map((item) =>
          item.product.product_id === product.product_id
            ? {
                ...item,
                quantity: Math.min(item.quantity + 1, product.stock),
              }
            : item
        )
      )
    } else {
      // Add new item
      setSelectedItems((prev) => [...prev, { product, quantity: 1 }])
    }
  }

  const handleQuantityChange = (productId, newQuantity) => {
    const quantity = Math.max(1, Math.min(Number(newQuantity), 999))
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.product.product_id === productId
          ? { ...item, quantity }
          : item
      )
    )
  }

  const handleRemoveItem = (productId) => {
    setSelectedItems((prev) => prev.filter((item) => item.product.product_id !== productId))
  }

  const calculateSubtotal = () => {
    return selectedItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!customerName.trim()) {
      newErrors.customerName = 'Customer name is required'
    }

    if (!customerPhone.trim()) {
      newErrors.customerPhone = 'Customer phone is required'
    } else if (!/^\+?\d{10,15}$/.test(customerPhone.trim())) {
      newErrors.customerPhone = 'Please enter a valid phone number'
    }

    if (!dueDate) {
      newErrors.dueDate = 'Due date is required'
    } else {
      const due = new Date(dueDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (due < today) {
        newErrors.dueDate = 'Due date cannot be in the past'
      }
    }

    if (!deposit || Number(deposit) <= 0) {
      newErrors.deposit = 'Deposit must be greater than 0'
    } else if (Number(deposit) > calculateSubtotal()) {
      newErrors.deposit = 'Deposit cannot exceed total amount'
    }

    if (selectedItems.length === 0) {
      newErrors.items = 'Please select at least one product'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please correct the errors in the form',
        confirmButtonColor: brandColors.primary,
      })
      return
    }

    if (!branchId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Branch ID is required. Please ensure you are assigned to a branch.',
        confirmButtonColor: brandColors.primary,
      })
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        branch_id: Number(branchId),
        due_date: dueDate,
        items: selectedItems.map((item) => ({
          product_id: item.product.product_id,
          quantity: item.quantity,
        })),
        deposit: Number(parseFloat(deposit).toFixed(2)),
        payment_method: paymentMethod,
        notes: notes.trim() || undefined,
      }

      const response = await apiClient.post(API_ENDPOINTS.LAYBY, payload)

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `Layby created successfully. Reference: ${response.data.layby_number || 'N/A'}`,
        confirmButtonColor: brandColors.primary,
      })

      // Reset form
      setCustomerName('')
      setCustomerPhone('')
      setDueDate('')
      setDeposit('')
      setPaymentMethod('cash')
      setNotes('')
      setSelectedItems([])
      setErrors({})

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Error creating layby:', error)
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to create layby. Please try again.'
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: brandColors.primary,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Set default due date to 30 days from now
  useEffect(() => {
    if (!dueDate) {
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 30)
      setDueDate(defaultDate.toISOString().split('T')[0])
    }
  }, [])

  const subtotal = calculateSubtotal()
  const balance = subtotal - (Number(deposit) || 0)

  return (
    <div className="row">
      <div className="col-lg-8">
        <form onSubmit={handleSubmit}>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="bi bi-person me-2" />
                Customer Information
              </h5>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Customer Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.customerName ? 'is-invalid' : ''}`}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter customer name"
                    required
                  />
                  {errors.customerName && (
                    <div className="invalid-feedback">{errors.customerName}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Customer Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${errors.customerPhone ? 'is-invalid' : ''}`}
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="+263771234567"
                    required
                  />
                  {errors.customerPhone && (
                    <div className="invalid-feedback">{errors.customerPhone}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Due Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.dueDate ? 'is-invalid' : ''}`}
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  {errors.dueDate && (
                    <div className="invalid-feedback">{errors.dueDate}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Payment Method <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    required
                  >
                    {paymentMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Notes (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this layby..."
                />
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="bi bi-bag me-2" />
                Select Products
              </h5>

              {productsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading products...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Search Products</label>
                    <Select
                      options={productOptions}
                      onChange={handleProductSelect}
                      placeholder="Search and select products..."
                      isClearable
                      isSearchable
                      noOptionsMessage={() => 'No products available'}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '42px',
                        }),
                      }}
                    />
                    {errors.items && (
                      <div className="text-danger small mt-1">{errors.items}</div>
                    )}
                  </div>

                  {selectedItems.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Price</th>
                            <th>Quantity</th>
                            <th>Subtotal</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedItems.map((item) => (
                            <tr key={item.product.product_id}>
                              <td>{item.product.name}</td>
                              <td>
                                <span className="badge bg-secondary">{item.product.sku}</span>
                              </td>
                              <td>${item.product.price.toFixed(2)}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: '80px' }}
                                  min="1"
                                  max={item.product.stock}
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.product.product_id,
                                      e.target.value
                                    )
                                  }
                                />
                                <small className="text-muted d-block">
                                  Max: {item.product.stock}
                                </small>
                              </td>
                              <td>${(item.product.price * item.quantity).toFixed(2)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRemoveItem(item.product.product_id)}
                                >
                                  <i className="bi bi-trash" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="bi bi-cash-coin me-2" />
                Payment Details
              </h5>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Deposit Amount <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className={`form-control ${errors.deposit ? 'is-invalid' : ''}`}
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value)}
                      min="0"
                      step="0.01"
                      max={subtotal}
                      required
                    />
                    {errors.deposit && (
                      <div className="invalid-feedback">{errors.deposit}</div>
                    )}
                  </div>
                  <small className="text-muted">
                    Maximum: ${subtotal.toFixed(2)}
                  </small>
                </div>
              </div>

              <div className="border-top pt-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-semibold">Subtotal:</span>
                  <span className="fw-bold">${subtotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Deposit:</span>
                  <span>${(Number(deposit) || 0).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between fw-bold fs-5 text-primary">
                  <span>Balance:</span>
                  <span>${balance.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setCustomerName('')
                setCustomerPhone('')
                setDueDate('')
                setDeposit('')
                setPaymentMethod('cash')
                setNotes('')
                setSelectedItems([])
                setErrors({})
              }}
            >
              Clear Form
            </button>
            <button
              type="submit"
              className="btn text-white"
              disabled={isSubmitting || selectedItems.length === 0}
              style={{
                background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                border: 'none',
              }}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Creating...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2" />
                  Create Layby
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="col-lg-4">
        <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
          <div className="card-body">
            <h6 className="card-title mb-3">Summary</h6>
            <div className="mb-3">
              <p className="text-muted small mb-1">Items Selected</p>
              <p className="fw-bold fs-4 mb-0">{selectedItems.length}</p>
            </div>
            <div className="mb-3">
              <p className="text-muted small mb-1">Total Quantity</p>
              <p className="fw-bold fs-4 mb-0">
                {selectedItems.reduce((sum, item) => sum + item.quantity, 0)}
              </p>
            </div>
            <div className="border-top pt-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <span className="fw-semibold">${subtotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Deposit:</span>
                <span>${(Number(deposit) || 0).toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between fw-bold fs-5 text-primary pt-2 border-top">
                <span>Balance:</span>
                <span>${balance.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateLayby
